/**
 * Parse one stored proof value without throwing on legacy invalid data.
 * @param {string|Array} proof stored proof JSON or an attachment array
 * @returns {Array} attachment list
 */
function getProofFiles (proof) {
  if (Array.isArray(proof)) {
    return proof;
  }
  if (!proof || typeof proof !== 'string') {
    return [];
  }
  try {
    const files = JSON.parse(proof);
    return Array.isArray(files) ? files : [];
  } catch (err) {
    return [];
  }
}

/**
 * Normalize an attachment flow ID for map matching.
 * @param {Object} file attachment metadata
 * @returns {string} normalized flow ID
 */
function getFlowId (file) {
  if (!file || (file.flowId === undefined || file.flowId === null || file.flowId === '')) {
    return '';
  }
  return `${file.flowId}`;
}

/**
 * Collect unique attachment flow IDs from one page of audit records.
 * @param {Array} records audit records
 * @returns {Array} unique flow IDs
 */
function collectProofFlowIds (records) {
  const ids = [];
  const seen = {};
  (records || []).forEach((record) => {
    getProofFiles(record && record.proof).forEach((file) => {
      const flowId = getFlowId(file);
      if (flowId && !seen[flowId]) {
        seen[flowId] = true;
        ids.push(flowId);
      }
    });
  });
  return ids;
}

/**
 * Resolve the best browser display URL from attachment metadata.
 * @param {Object} file attachment metadata
 * @returns {string} display URL
 */
function getProofDisplayUrl (file) {
  if (!file) {
    return '';
  }
  return file.fullPathUrl || file.fullPath || file.picFullUrl || file.url || file.storePath || file.picUrl || '';
}

/**
 * Normalize the supported bigFileQuery response wrappers.
 * @param {Object|Array} result file-query response
 * @returns {Array} file metadata rows
 */
function getFileQueryRows (result) {
  if (Array.isArray(result)) {
    return result;
  }
  if (!result || typeof result !== 'object') {
    return [];
  }
  if (Array.isArray(result.rows)) {
    return result.rows;
  }
  if (Array.isArray(result.items)) {
    return result.items;
  }
  if (result.data) {
    return getFileQueryRows(result.data);
  }
  return [];
}

/**
 * Return cloned audit records with current signed URLs matched by flow ID.
 * @param {Array} records audit records
 * @param {Object|Array} fileQueryResult file-query response
 * @returns {Array} refreshed audit records
 */
function refreshRecordProofUrls (records, fileQueryResult) {
  const fileMap = {};
  getFileQueryRows(fileQueryResult).forEach((file) => {
    const flowId = getFlowId(file);
    if (flowId) {
      fileMap[flowId] = file;
    }
  });
  return (records || []).map((record) => {
    const files = getProofFiles(record && record.proof);
    if (!files.length) {
      return { ...record };
    }
    const refreshedFiles = files.map((file) => {
      const current = { ...file };
      const serverFile = fileMap[getFlowId(file)];
      if (!serverFile) {
        return current;
      }
      const currentUrl = getProofDisplayUrl(serverFile);
      if (currentUrl) {
        current.picFullUrl = currentUrl;
      }
      current.picUrl = serverFile.storePath || serverFile.picUrl || current.picUrl;
      return current;
    });
    return {
      ...record,
      proof: JSON.stringify(refreshedFiles),
    };
  });
}

/**
 * Detect PDF attachments while ignoring URL query strings and fragments.
 * @param {Object} file attachment metadata
 * @returns {boolean} whether the attachment is a PDF
 */
function isPdfFile (file) {
  const url = getProofDisplayUrl(file);
  if (!url || typeof url !== 'string') {
    return false;
  }
  const path = url.split('?')[0].split('#')[0].toLowerCase();
  return path.substring(path.lastIndexOf('.') + 1) === 'pdf';
}

module.exports = {
  collectProofFlowIds,
  getFileQueryRows,
  getProofDisplayUrl,
  getProofFiles,
  isPdfFile,
  refreshRecordProofUrls,
};
